import { BookmakerName } from '@/constants';
import { AbstractModel } from '@/models/abstractModel';
import { useBookmakerDictionary } from '@/stores/remoteDictionary/bookmaker';
import type { IAlertRow } from '@/types/alert';

const PLACEHOLDER_REGEX = /\{([^}=]+)=([^}]*)\}/g;

export class AlertItem extends AbstractModel {
    public accountName: string;
    public betBalancePc: string | null;
    public betCreatedAt: string | null;
    public betHandicap: string | number | null;
    public betInternalId: number | null;
    public betOdds: string | null;
    public betRef: string | null;
    public betResult: string | null;
    public betSelectionId: number | null;
    public betSiteId: number | null;
    public code: string;
    public comment: string | null;
    public count: number;
    public createdAt: string;
    public customTeamAwayId: number;
    public customTeamAwayName: string;
    public customTeamHomeId: number;
    public customTeamHomeName: string;
    public dataId: number | null;
    public dismissToolUserId: number | null;
    public id: number;
    public isDismissedRaw: number;
    public isInverted: number;
    public isInvestigate: number;
    public nameId: number;
    public notificationSentAt: string | null;
    public priority: number;
    public priorityId: number | null;
    public teamAwayId: number;
    public teamAwayName: string;
    public teamHomeId: number;
    public teamHomeName: string;
    public text: string | null;
    public typeId: number;
    public uniq: string;
    public uniqAccountId: number;
    public uniqBetId: number | null;
    public uniqBetOrderId: number | null;
    public uniqBotId: number | null;
    public uniqEventId: number;
    public uniqSiteId: number;
    public uniqToolUserId: number | null;
    public uniqUserName: string | null;
    public uniqUserPlatformId: number | null;
    public updatedAt: string | null;
    public userName: string;

    constructor(data: IAlertRow) {
        super();
        this.id = data.id;
        this.createdAt = data.created_at;
        this.updatedAt = data.updated_at ?? null;
        this.notificationSentAt = data.notification_sent_at ?? null;
        this.typeId = data.type_id;
        this.code = data.code;
        this.uniq = data.uniq;
        this.nameId = data.name_id;
        this.count = data.count;
        this.isDismissedRaw = data.is_dismissed;
        this.isInvestigate = data.is_investigate;
        this.priorityId = data.priority_id ?? null;
        this.text = data.text ?? null;
        this.comment = data.comment ?? null;
        this.uniqToolUserId = data.uniq_tool_user_id ?? null;
        this.uniqSiteId = data.uniq_site_id;
        this.uniqEventId = data.uniq_event_id;
        this.uniqBetId = data.uniq_bet_id ?? null;
        this.uniqAccountId = data.uniq_account_id;
        this.uniqBotId = data.uniq_bot_id ?? null;
        this.uniqBetOrderId = data.uniq_bet_order_id ?? null;
        this.dismissToolUserId = data.dismiss_tool_user_id ?? null;
        this.priority = data.priority;
        this.userName = data.user_name ?? '';
        this.uniqUserName = data.uniq_user_name ?? null;
        this.uniqUserPlatformId = data.uniq_user_platform_id ?? null;
        this.dataId = data.data_id ?? null;
        this.accountName = data.account_name ?? '';
        this.isInverted = data.isInverted ?? 0;
        this.teamHomeName = data.team_home_name ?? '';
        this.teamHomeId = data.team_home_id;
        this.teamAwayName = data.team_away_name ?? '';
        this.teamAwayId = data.team_away_id;
        this.customTeamHomeId = data.custom_team_home_id;
        this.customTeamHomeName = data.custom_team_home_name ?? '';
        this.customTeamAwayId = data.custom_team_away_id;
        this.customTeamAwayName = data.custom_team_away_name ?? '';
        this.betInternalId = data.bet_internal_id ?? null;
        this.betRef = data.bet_ref ?? null;
        this.betSelectionId = data.bet_selection_id ?? null;
        this.betHandicap = data.bet_handicap ?? null;
        this.betOdds = data.bet_odds ?? null;
        this.betResult = data.bet_result ?? null;
        this.betBalancePc = data.bet_balance_pc ?? null;
        this.betCreatedAt = data.bet_created_at ?? null;
        this.betSiteId = data.bet_site_id ?? null;
    }

    get bookmakerName(): string {
        return BookmakerName[this.uniqSiteId];
    }

    get displayText(): string {
        const text = this.text;
        if (!text) return '';
        if (this.betInternalId == null) return text;
        if (!text.includes('{')) return text;
        return text.replace(PLACEHOLDER_REGEX, (_, key: string, value: string) => this.getPlaceholderReplacement(key.trim(), value.trim()));
    }

    get gameAwayDisplay(): string {
        return this.customTeamAwayName || this.teamAwayName || '';
    }

    get gameDisplay(): string {
        const home = this.gameHomeDisplay;
        const away = this.gameAwayDisplay;
        if (home && away) return `${home} vs ${away}`;
        return home || away || '';
    }

    get gameHomeDisplay(): string {
        return this.customTeamHomeName || this.teamHomeName || '';
    }

    get isDismissed(): boolean {
        return this.isDismissedRaw === 1;
    }

    private formatBetTemplate(): string {
        const siteCode = useBookmakerDictionary().itemById(this.betSiteId!).code;
        const handicap = this.betHandicap != null ? String(this.betHandicap) : '';
        const parts = [this.betSelectionId, handicap, this.betOdds, this.betResult, this.betBalancePc].filter(Boolean);
        return ` ${siteCode} bet #${this.betInternalId}, ref ${this.betRef}, ${parts.join(' ')}, ${this.betCreatedAt} `;
    }

    private getPlaceholderReplacement(key: string, value: string): string {
        if (key === 'betId') return this.formatBetTemplate();
        if (key === 'toolUserId') return this.uniqUserName ?? '';
        return value;
    }
}
